import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'

// Constants
import { PATH_FORGOT_PASSWORD, PATH_HOME, PATH_LOGIN } from 'routes/paths'
import { PROVIDERS } from 'constants/keys'

// Services
import dialog from 'services/dialog'
import loadingService from 'services/loading'

// Helpers
import get from 'lodash/get'
import {
  getReferrer,
  getSocialToken,
  removeReferrer,
  removeSocialToken,
} from 'helpers/auth'
import { resolveErrorMessage } from 'helpers'

// Hooks
import { useFormHook } from 'use/form'
import { useHistory } from 'react-router'
import useQuery from 'use/query'
import { useDesktop } from 'use/media'

// Api
import { login, loginWithSocial, mergeSocialAccount } from 'api/auth'

// Components
import Row from 'ui/Row'
import Col from 'ui/Col'
import Input from 'ui/Form/Input'
import Link from 'ui/Link'
import Button from 'ui/Button'
import Space from 'ui/Space'
import Heading from 'ui/Heading'
import Box from 'ui/Box'
import Text from 'ui/Text'
import IconFinger from 'ui/Icons/IconFinger'
import PopupMergeAccount from './PopupMergeAccount'
import PopupOTP from './PopupOTP'
import PopupResetPassword from './PopupResetPassword'
import ShowInputPassword from 'ui/Form/ShowInputPassword'

// Validate schema
const validationSchema = yup.object().shape({
  email: yup
    .string()
    .trim()
    .required('The email field is required')
    .email('Invalid email address'),
  password: yup.string().trim().required('The password field is required'),
})

const resolveLabel = (value) => {
  const provider = PROVIDERS.find((x) => x.provider === value)
  return provider && provider.label
}

export const LoginForm = () => {
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const { provider, token, merge } = useQuery()
  const isDesktop = useDesktop()

  // Form hook
  const {
    form,
    errors,
    validate,
    onChange,
    onBlur,
    setFieldError,
    setFieldValue,
  } = useFormHook({
    schema: validationSchema,
    initialForm: {
      email: '',
      password: '',
      provider: null,
      socialToken: null,
    },
  })

  const isMergingTraditional = useMemo(() => {
    return !!form.provider && !!form.socialToken
  }, [form])

  const mergeTranditional = useCallback(
    (provider, token) => {
      setFieldValue('provider', provider)
      setFieldValue('socialToken', token)
    },
    [setFieldValue]
  )

  const onLoginSuccess = useCallback(() => {
    const referrer = getReferrer()
    history.replace(referrer ? referrer : PATH_HOME)

    // Clear referrer
    if (referrer) {
      removeReferrer()
    }
  }, [history])

  useEffect(() => {
    if (provider && token) {
      loadingService.show()

      // Case MERGE
      if (merge) {
        const existedToken = getSocialToken()

        if (existedToken) {
          // Merge social -> social
          mergeSocialAccount(existedToken, token)
            .then(() => {
              onLoginSuccess()
            })
            .catch(async (error) => {
              dialog.alert({
                title: 'Sorry!',
                message: await resolveErrorMessage(error),
                onConfirm: ({ setVisible }) => {
                  setVisible(false)
                  history.replace(PATH_LOGIN)
                },
              })
            })
            .finally(() => {
              loadingService.hide()
            })
        } else {
          dialog.alert({
            title: 'Sorry!',
            message: 'Missing previous social token',
            onConfirm: ({ setVisible }) => {
              setVisible(false)
              history.replace(PATH_LOGIN)
            },
          })
          loadingService.hide()
        }

        return
      }

      // CASE LOGIN
      loginWithSocial(provider, token)
        .then(() => {
          onLoginSuccess()
        })
        .catch(async (error) => {
          const code = get(error, 'response.data.code')
          const existingProvider = get(error, 'response.data.existingProvider')

          if (code === 'SOCIAL.RECORD.ALREADYREGISTERED') {
            dialog.create(PopupMergeAccount, {
              existingProvider,
              newProvider: provider,
              token,
              mergeTranditional,
            })

            return
          }

          dialog.alert({
            title: await resolveErrorMessage(error),
          })
        })
        .finally(() => {
          loadingService.hide()
        })
    }
  }, [history, merge, provider, token, mergeTranditional, onLoginSuccess])


  const onSubmitClick = async () => {
    try {
      await validate()
    } catch (err) {
      //
      return
    }

    try {
      setLoading(true)
      const isOTP = await login(form, isMergingTraditional ? null : form.email)

      if (isOTP) {
        // Show otp popup
        dialog.create(PopupOTP, {
          onConfirm: ({ setVisible }) => {
            setVisible(false)
            onLoginSuccess()
          },
        })
      } else {
        removeSocialToken()
        onLoginSuccess()
      }
    } catch (error) {
      // Clear error
      setFieldError('email', '')
      setFieldError('password', '')

      const code = get(error, 'response.data.code')

      // Email not existed
      if (code === 'A010') {
        setFieldError('email', 'Email address & password do not match')
        return
      }

      // Wrong password
      if (code === 'SIGNIN.INVALIDCREDENTIAL') {
        setFieldError('email', ' ')
        setFieldError('password', 'Email address & password do not match')
        return
      }

      // Login limit
      if (code === 'SIGNIN.RATELIMIT.EXCEEDED') {
        dialog.alert({
          title: 'Too many failed login attempts.',
          message: 'Please try again later.',
        })
        return
      }
      // Account Does Not Exist
      if (code === 'SIGNIN.ACCOUNT.NOT.FOUND') {
        dialog.alert({
          title: 'User not found in the System.',
          message: 'Please contact Admin.',
        })
        return
      }
      // Account Whitelist
      if (code === 'SIGNIN.RESET.PASSWORD.REQUIRED') {
        dialog.create(PopupResetPassword, {
          emailReceiver: form.email,
          onConfirm: ({ setVisible }) => {
            setVisible(false)
          },
        })
        return
      }

      // Force change password
      if (code === 426) {
        dialog.confirm({
          title: 'Your password is expired',
          message: 'Please reset your password to continue',
          confirmText: 'Reset',
          onConfirm: ({ setVisible }) => {
            setVisible(false)
            history.push(PATH_FORGOT_PASSWORD)
          },
        })
        return
      }

      // Any other error
      setFieldError('password', await resolveErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      onSubmitClick()
    }
  }

  return (
    <>
      <Row noGutters onKeyDown={onKeyDown}>
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 11, offset: 0 }}
        >
          {isMergingTraditional ? (
            <>
              <Text bold color="#000000" size={23} paddingBottom={10}>
                Link {resolveLabel(form.provider)} account
              </Text>
              <Box
                backgroundColor="#ecf4fc"
                style={{
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '15px',
                  lineHeight: '25px',
                }}
              >
                <Text color="#1351AA" bold size={17}>
                  One credential for your Globe apps!
                </Text>
                <Text color="#000000" size={16}>
                  To finish linking account with your Globe Login account, log
                  in using the email registered in another Globe app (eg.
                  GlobeONE or TM app)
                </Text>
                <Text color="#000000" size={14}>
                  Note: Credentials may not the same as the email and password
                  you use to login to Google, Facebook or Yahoo.
                </Text>
              </Box>
            </>
          ) : (
            <>
              <Heading
                size={isDesktop ? '30px' : '20px'}
                paddingTop={0}
                paddingBottom={0}
              >
                Login to your account
              </Heading>
              <Box
                backgroundColor="#ecf4fc"
                style={{
                  borderRadius: '8px',
                  padding: '20px 25px',
                  position: 'relative',
                  marginTop: '0px',
                  marginBottom: '10px',
                }}
              >
                <Space>
                  {PROVIDERS.map((x, index) => {
                    return <x.icon key={index} width={26} height={26}></x.icon>
                  })}
                </Space>
                <Text color="#1351aa" size={14} bold>
                  We have decomissioned our social media account login
                </Text>
                <Text color="#000000" size={12}>
                  If you&apos;ve been using it, enter your registered email
                  address to proceed. If not, please enter your credentials.
                </Text>
                <IconFinger
                  style={{
                    position: 'absolute',
                    left: '-25px',
                    top: '30%',
                    transform: 'translateY(-50%)',
                  }}
                ></IconFinger>
              </Box>
            </>
          )
          }
        </Col>
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 11, offset: 0 }}
        >
          <Input
            name="email"
            value={form.email}
            label="E-mail address"
            errors={errors}
            placeholder="Enter your e-mail address"
            autoComplete="off"
            style={{ width: '100%' }}
            onChange={onChange}
            onBlur={onBlur}
          />
        </Col>
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 11, offset: 0 }}
        >
          <ShowInputPassword
            name="password"
            value={form.password}
            label="Password"
            errors={errors}
            placeholder="Enter password here"
            autoComplete="off"
            style={{ width: '100%' }}
            onChange={onChange}
            onBlur={onBlur}
          />
        </Col>
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 11, offset: 0 }}
        >
          <Link
            underline
            path="/password/forgot"
            disabled={loading}
            style={{
              pointerEvents: loading ? 'none' : 'inherit',
            }}
          >
            Forgot your password?
          </Link>
        </Col>
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 11, offset: 0 }}
        >
          <br></br>
          <Button block onClick={onSubmitClick} loading={loading}>
            Login
          </Button>
          <br></br>
        </Col>
      </Row>
    </>
  )
}

LoginForm.propTypes = {
  onSubmit: PropTypes.func,
}

LoginForm.defaultProps = {
  onSubmit: () => {},
}

export default LoginForm
